// Builds the read-only answer cards per journey step from the tenant schema + saved risk data
// (the "your answers" half of the quote-details view, without the quote/cover tab).
import { ref } from 'vue';
import { getQuoteProductConfig } from '../config/productRegistry';
import { buildStepCards, type SummarySection } from './schemaSummary';
import { canonicaliseSchemaValues } from '@/utils/canonicaliseSchemaValues';

export type AnswerStep = { id: string; name: string; cards: SummarySection[] };

type DetailsStepLayout = { splitByHeadings?: boolean; fullWidth?: boolean; labelWidth?: string; cards?: { key: string; span?: number }[] };

// Per-tenant, per-step layout: reorder cards and set column spans (2 = full width).
function applyDetailsLayout(cards: SummarySection[], layout?: DetailsStepLayout): SummarySection[] {
    if (!layout) return cards;
    if (layout.labelWidth) cards.forEach((c) => { c.labelWidth = layout.labelWidth; });
    if (layout.cards?.length) {
        const order = new Map(layout.cards.map((c, i) => [c.key, i]));
        const spans = new Map(layout.cards.map((c) => [c.key, c.span ?? (layout.fullWidth ? 2 : 1)]));
        const ordered = [...cards].sort((a, b) => (order.get(a.key ?? '') ?? 999) - (order.get(b.key ?? '') ?? 999));
        ordered.forEach((c) => { c.span = spans.get(c.key ?? '') ?? (layout.fullWidth ? 2 : 1); });
        return ordered;
    }
    if (layout.fullWidth) cards.forEach((c) => { c.span = 2; });
    return cards;
}

// Unwrap a legacy `{ riskData: {...} }` envelope; the root otherwise holds the fields directly.
function unwrapRiskData(raw: any): any {
    if (!raw || typeof raw !== 'object') return {};
    if (raw.RiskQuestions === undefined && raw.riskData && typeof raw.riskData === 'object') return raw.riskData;
    return raw;
}

export function useSchemaAnswerSteps() {
    const loading = ref(true);
    const error = ref<string | null>(null);
    const steps = ref<AnswerStep[]>([]);

    async function load(productId: string, riskDataRaw: any) {
        loading.value = true;
        error.value = null;

        const productConfig = getQuoteProductConfig(productId);
        if (!productConfig) {
            error.value = 'No quote display is configured for this product.';
            loading.value = false;
            return;
        }

        try {
            const config = (await productConfig.loadSchema()).default;
            const customLayout = !!config.detailsLayout;
            const formSchema = config.getFormFields();
            const schemaSteps: any[] = config.steps ?? [];
            // Same casing repair the journey does on load, or condition filtering hides answers from the summary.
            const riskData = canonicaliseSchemaValues(formSchema, unwrapRiskData(riskDataRaw)).data;

            // A schemaKey reused by a later step (e.g. a review page) is a duplicate — keep the last only.
            const lastIndexForKey = new Map<string, number>();
            schemaSteps.forEach((step, i) => { if (step?.schemaKey && !step.hide) lastIndexForKey.set(step.schemaKey, i); });

            const built: AnswerStep[] = [];
            for (let i = 0; i < schemaSteps.length; i++) {
                const step = schemaSteps[i];
                if (!step || step.hide || !step.schemaKey) continue;
                if (lastIndexForKey.get(step.schemaKey) !== i) continue;

                const stepSchema = formSchema[step.schemaKey];
                if (!stepSchema) continue;

                let cards: SummarySection[];
                if (customLayout) {
                    const layout: DetailsStepLayout | undefined = config.detailsLayout?.[step.id];
                    cards = applyDetailsLayout(buildStepCards(stepSchema, riskData, step.name, { splitByHeadings: !!layout?.splitByHeadings, keyed: true }), layout);
                } else {
                    cards = buildStepCards(stepSchema, riskData, step.name);
                }
                if (cards.length) built.push({ id: step.id, name: step.name, cards });
            }

            steps.value = built;
            if (!built.length) error.value = 'No quote details are available to display.';
        } catch (e) {
            console.error('Failed to build quote answer steps:', e);
            error.value = 'Unable to load the quote details.';
        } finally {
            loading.value = false;
        }
    }

    return { loading, error, steps, load };
}
